import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { offset } from 'caret-pos';
import styled, { css } from 'styled-components';

import { getDeviceType } from '../../lib/utils';

import CrossIconPath from './assets/stroked-cross.svg?react';

import SpecialCharTooltip, { specialChars } from '../special-char-tooltip';

import { useOnClickOutside } from '../../lib/hooks';

export const Container = styled.div`
    position: relative;
`;

export const Answer = styled.textarea`
    font-size: 16px;
    box-sizing: border-box;
    border: var(--border-size-small) solid var(--gray200);
    color: var(--gray800);
    background-color: var(--color-white);
    min-height: 184px;
    width: 100%;
    resize: none;
    padding: calc(var(--space-unit) / 2);
    font-family: 'IBM Plex Sans', sans-serif;
    overflow: hidden;
    border-radius: 0;

    ${(props) =>
        !props.disabled &&
        css`
            box-shadow: inset var(--shadow-2);
        `}

    &:not(:focus) {
        ${(props) =>
            !props.disabled &&
            props.value &&
            css`
                border: var(--border-size-small) solid var(--blue400);
                background: var(--blue50);
                color: var(--gray800);
            `}
    }

    &:hover {
        ${(props) =>
            !props.value &&
            !props.disabled &&
            css`
                border: var(--border-size-small) solid var(--gray800);
                background: var(--gray50);
                box-shadow: inset var(--shadow-2);
                cursor: pointer;
            `}
        ${(props) =>
            props.value &&
            !props.disabled &&
            css`
                border: var(--border-size-small) solid var(--blue700);
                color: var(--blue700);
                box-shadow: inset var(--shadow-2);
                cursor: pointer;
            `}
    }

    &:active,
    &:focus {
        cursor: text;
        color: var(--blue400);
        border: var(--border-size-small) solid var(--blue400);
        background: var(--color-white);
        box-shadow: var(--outline);
        outline: none;
        padding-right: calc(var(--space-unit) * 4);
    }

    &:disabled {
        pointer-events: none;
    }

    ${(props) =>
        !props.disabled &&
        css`
            box-shadow: inset var(--shadow-2);
        `}
`;

export const CrossIconButton = styled.button`
    background: transparent;
    border: none;
    outline: none;

    cursor: pointer;

    height: calc(var(--space-unit) * 4);
    width: calc(var(--space-unit) * 4);

    position: absolute;
    top: 0;
    right: 0;

    ${Container}:not(:focus-within) & {
        opacity: 0;
    }
`;

export const CrossIcon = styled(CrossIconPath)`
    ${({ theme }) => css`
        fill: ${theme.colors['surface-grey-700']};
    `}
`;

const OpenAnswerTextarea = ({
    disabled = false,
    id,
    onCancelClick = () => {},
    onChange = () => {},
    placeholder = '',
    style,
    value = '',
}) => {
    const textAreaRef = useRef();
    const componentRef = useRef();
    const isDesktop = getDeviceType() === 'DESKTOP';
    const [show, setShow] = useState(false);
    const [caretPosition, setCaretPosition] = useState({ left: 10, top: 10 });
    const [char, setChar] = useState('');
    const [up, setUp] = useState('');
    const [prevChar, setPrevChar] = useState('');

    const handleKeyDown = (e) => {
        setShow(false);
        const insertedChar = e.key;
        const currentChar = insertedChar.toLowerCase() in specialChars ? insertedChar : null;

        if (!!currentChar && currentChar === prevChar && currentChar !== up) {
            // Long press
            e.preventDefault();
            setShow(true);
            setChar(insertedChar);
            const currentCaretPosition = offset(textAreaRef.current);
            setCaretPosition(currentCaretPosition);
        } else if (
            !isNaN(insertedChar) &&
            specialChars[char.toLowerCase()] &&
            parseInt(insertedChar) <= specialChars[char.toLowerCase()].length
        ) {
            // Number key pressed when tooltip open
            e.preventDefault();
            const uppercase = char === char.toUpperCase();
            const key = specialChars[char.toLowerCase()][parseInt(insertedChar) - 1];
            setChar('');
            handleClose(uppercase ? key.toUpperCase() : key);
        } else {
            setChar('');
            setUp('');
            onChange(e);
        }
        setPrevChar(currentChar);
    };

    useEffect(() => {
        textAreaRef.current.style.height = `auto`;
        textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
    }, [value]);

    useOnClickOutside(componentRef, () => {
        setShow(false);
    });

    const handleClose = (key) => {
        const { value: textAreaValue, selectionStart, selectionEnd } = textAreaRef.current;
        const text = textAreaValue.substring(0, selectionStart - 1) + key + textAreaValue.substring(selectionEnd, textAreaValue.length);
        onChange({ target: { value: text } });
        setShow(false);
        textAreaRef.current.focus();
    };

    return (
        <div ref={componentRef}>
            <Container>
                <Answer
                    ref={textAreaRef}
                    onChange={onChange}
                    onKeyDown={handleKeyDown}
                    value={value}
                    disabled={disabled}
                    placeholder={placeholder}
                    style={style}
                    onKeyUp={(e) => {
                        setUp(e.key);
                    }}
                    id={id}
                />
                {!disabled && value && (
                    <CrossIconButton type="button" aria-label="Cancella tutto il testo" onClick={onCancelClick}>
                        <CrossIcon aria-hidden="true" />
                    </CrossIconButton>
                )}
            </Container>
            {isDesktop && show && (
                <SpecialCharTooltip handleClose={handleClose} target={textAreaRef.current} char={char} position={caretPosition} />
            )}
        </div>
    );
};

OpenAnswerTextarea.propTypes = {
    disabled: PropTypes.bool,
    id: PropTypes.string,
    onCancelClick: PropTypes.func,
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    style: PropTypes.object,
    value: PropTypes.string,
};

export default OpenAnswerTextarea;
